import type {
  ApiError,
  AdminStayCreate,
  AdminStayUpdate,
  ExternalStayInput,
  PricesUpdate,
  StayInput,
} from '@baerenfang/shared';

export type { AdminStayCreate, ExternalStayInput, PricesUpdate } from '@baerenfang/shared';

export type AvailabilityCell = {
  date: string;
  total_beds: number;
  beds_available: number;
  is_override: boolean;
  has_duty: boolean;
  /** Warden names on duty this date, ordered (case-insensitive) by
   *  name. Empty when no one is on duty. */
  duty_names: string[];
};

export type HutDutySlot = {
  id: number;
  check_in: string;
  check_out: string;
  note: string | null;
  // One non-negative integer per night the slot covers, in canonical
  // date order. Length matches `nightDatesBetween(check_in, check_out)`.
  beds_per_night: number[];
  // One boolean per calendar day the slot covers (inclusive both ends).
  // True = "Hütte Bewartet" marker on that day. Length matches
  // `dayDatesInclusive(check_in, check_out)` = beds_per_night.length + 1.
  is_duty_per_day: boolean[];
};

export type GuestSession = {
  name: string;
  hut_capacity_default: number;
  slots: HutDutySlot[];
  prices: PricesUpdate;
};

export type AdminSession = { username: string };

/** External-form session marker. The form carries name/contact in its body,
 * so we don't need to fetch any state — the cookie's existence is the
 * session. */
export type ExternalSession = { kind: 'external' };

export type StayCreated = { ok: true; id: number };

export type CapacityDetails = Record<string, { requested: number; available: number }>;

export type HutDuty = {
  id: number;
  name: string;
  active: number;
  note: string | null;
  login_alias: string | null;
  email: string | null;
  created_at: string;
  slots: HutDutySlot[];
};

export type AdminCalendarStay = {
  stay_id: number;
  name: string;
  members_adult: number;
  members_child: number;
  guests_adult: number;
  guests_child: number;
  notes: string | null;
};

export type AdminCalendarDuty = {
  slot_id: number;
  name: string;
  is_duty: boolean;
  note: string | null;
  beds: number;
};

export type AdminCalendarCell = {
  date: string;
  total_beds: number;
  occupied: number;
  note: string | null;
  stays: AdminCalendarStay[];
  duties: AdminCalendarDuty[];
};

export type AdminStaySummary = {
  id: number;
  name: string;
  check_in: string;
  check_out: string;
  stayover_rappen: number;
  beverages_rappen: number;
  notes: string | null;
  submitted_ip: string | null;
  edited_at: string | null;
  paid_at: string | null;
  pay_twint: number;
  pay_slip: number;
  pay_cash: number;
  surname: string | null;
  street: string | null;
  zipcode: string | null;
  town: string | null;
  email: string | null;
  is_external: number;
  created_at: string;
  // Person-night sums by category (rolled up from stay_nights in the SQL).
  sum_members_adult: number;
  sum_members_child: number;
  sum_guests_adult: number;
  sum_guests_child: number;
};

export type AdminStayNight = {
  stay_id: number;
  date: string;
  members_adult: number;
  members_child: number;
  guests_adult: number;
  guests_child: number;
};

export type AdminStayDetail = AdminStaySummary & { nights: AdminStayNight[] };

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, payload: ApiError['error']) {
    super(payload.message ?? payload.code);
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  let body: ApiError | null = null;
  try {
    body = (await res.json()) as ApiError;
  } catch {
    /* body may not be JSON */
  }
  throw new ApiClientError(
    res.status,
    body?.error ?? { code: 'ERR_INTERNAL', message: 'Unexpected response' },
  );
}

const jsonRequest = async (
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
  signal?: AbortSignal,
): Promise<Response> =>
  fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

/* ─── public ─────────────────────────────────────────────────────────── */

export async function fetchAvailability(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<AvailabilityCell[]> {
  return unwrap<AvailabilityCell[]>(
    await jsonRequest(`/api/public/availability?from=${from}&to=${to}`, 'GET', undefined, signal),
  );
}

/* ─── guest ──────────────────────────────────────────────────────────── */

export async function fetchMe(signal?: AbortSignal): Promise<GuestSession> {
  return unwrap<GuestSession>(await jsonRequest('/api/me', 'GET', undefined, signal));
}

export async function loginGuest(name: string, password: string): Promise<{ name: string }> {
  return unwrap<{ name: string }>(
    await jsonRequest('/api/auth/guest', 'POST', { name, password }),
  );
}

export async function logout(): Promise<void> {
  await jsonRequest('/api/auth/logout', 'POST');
}

export async function submitStay(payload: StayInput): Promise<StayCreated> {
  return unwrap<StayCreated>(await jsonRequest('/api/stays', 'POST', payload));
}

/* ─── external (password-gated, off-roster) ─────────────────────────── */

export async function loginExternal(password: string): Promise<void> {
  await unwrap<{ ok: true }>(
    await jsonRequest('/api/auth/external', 'POST', { password }),
  );
}

export async function fetchExternalMe(signal?: AbortSignal): Promise<ExternalSession> {
  await unwrap<{ ok: true; role: 'external' }>(
    await jsonRequest('/api/external/me', 'GET', undefined, signal),
  );
  return { kind: 'external' };
}

export async function submitExternalStay(
  payload: ExternalStayInput,
): Promise<StayCreated> {
  return unwrap<StayCreated>(await jsonRequest('/api/external/stays', 'POST', payload));
}

/* ─── admin ──────────────────────────────────────────────────────────── */

export async function fetchAdminMe(signal?: AbortSignal): Promise<AdminSession> {
  return unwrap<AdminSession>(await jsonRequest('/api/admin/me', 'GET', undefined, signal));
}

export async function loginAdmin(username: string, password: string): Promise<void> {
  await unwrap<{ ok: true }>(
    await jsonRequest('/api/auth/admin', 'POST', { username, password }),
  );
}

export type NewSlotInput = {
  check_in: string;
  check_out: string;
  note: string | null;
  // Per-night bed counts. Length must match
  // `nightDatesBetween(check_in, check_out)` on the server. Optional/empty
  // = no beds reserved.
  beds_per_night?: number[];
  // Per-day duty markers (inclusive both ends). Length must match
  // `dayDatesInclusive(check_in, check_out)` on the server. Optional/empty
  // = no duty marker on any day.
  is_duty_per_day?: boolean[];
};

export async function listHutDuties(signal?: AbortSignal): Promise<HutDuty[]> {
  return unwrap<HutDuty[]>(
    await jsonRequest('/api/admin/hut-duties', 'GET', undefined, signal),
  );
}

export async function createHutDuty(
  name: string,
  note: string | null,
  slot: NewSlotInput,
  login_alias?: string | null,
): Promise<HutDuty> {
  return unwrap<HutDuty>(
    await jsonRequest('/api/admin/hut-duties', 'POST', { name, note, slot, login_alias }),
  );
}

export async function backfillHutDutyAliases(): Promise<{ filled: number }> {
  return unwrap<{ filled: number }>(
    await jsonRequest('/api/admin/hut-duties/backfill-aliases', 'POST'),
  );
}

export type BulkHutDutyEntry = { name: string; slot?: NewSlotInput };

export async function bulkHutDuties(
  entries: BulkHutDutyEntry[],
): Promise<{ added: number; skipped: number }> {
  return unwrap<{ ok: true; added: number; skipped: number }>(
    await jsonRequest('/api/admin/hut-duties/bulk', 'POST', { entries }),
  );
}

export async function patchHutDuty(
  id: number,
  patch: Partial<{
    name: string;
    active: boolean;
    note: string | null;
    login_alias: string | null;
    email: string | null;
  }>,
): Promise<HutDuty> {
  return unwrap<HutDuty>(
    await jsonRequest(`/api/admin/hut-duties/${id}`, 'PATCH', patch),
  );
}

// `cascade` opts in to destroying the name's hut-duty slots along with it.
// Without it the server refuses as soon as the name has any slots, answering
// ERR_NAME_HAS_DUTY_SLOTS with `{ slots }` in the error details.
export async function deleteHutDuty(id: number, opts?: { cascade?: boolean }): Promise<void> {
  const qs = opts?.cascade ? '?cascade=true' : '';
  await unwrap<{ ok: true }>(
    await jsonRequest(`/api/admin/hut-duties/${id}${qs}`, 'DELETE'),
  );
}

export async function addSlot(dutyId: number, slot: NewSlotInput): Promise<HutDutySlot> {
  return unwrap<HutDutySlot>(
    await jsonRequest(`/api/admin/hut-duties/${dutyId}/slots`, 'POST', slot),
  );
}

export async function patchSlot(
  dutyId: number,
  slotId: number,
  slot: NewSlotInput,
): Promise<HutDutySlot> {
  return unwrap<HutDutySlot>(
    await jsonRequest(`/api/admin/hut-duties/${dutyId}/slots/${slotId}`, 'PATCH', slot),
  );
}

export async function deleteSlot(dutyId: number, slotId: number): Promise<void> {
  await unwrap<{ ok: true }>(
    await jsonRequest(`/api/admin/hut-duties/${dutyId}/slots/${slotId}`, 'DELETE'),
  );
}

export async function fetchAdminCalendar(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<AdminCalendarCell[]> {
  return unwrap<AdminCalendarCell[]>(
    await jsonRequest(`/api/admin/calendar?from=${from}&to=${to}`, 'GET', undefined, signal),
  );
}

export async function fetchAdminStays(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<AdminStaySummary[]> {
  return unwrap<AdminStaySummary[]>(
    await jsonRequest(`/api/admin/stays?from=${from}&to=${to}`, 'GET', undefined, signal),
  );
}

export async function fetchAdminStayDetail(
  id: number,
  signal?: AbortSignal,
): Promise<AdminStayDetail> {
  return unwrap<AdminStayDetail>(
    await jsonRequest(`/api/admin/stays/${id}`, 'GET', undefined, signal),
  );
}

export async function updateAdminStay(
  id: number,
  payload: AdminStayUpdate,
): Promise<void> {
  await unwrap<{ ok: true; id: number }>(
    await jsonRequest(`/api/admin/stays/${id}`, 'PUT', payload),
  );
}

export async function createAdminStay(
  payload: AdminStayCreate,
): Promise<{ id: number }> {
  return unwrap<{ ok: true; id: number }>(
    await jsonRequest('/api/admin/stays', 'POST', payload),
  );
}

export async function deleteAdminStay(id: number): Promise<void> {
  await unwrap<{ ok: true }>(
    await jsonRequest(`/api/admin/stays/${id}`, 'DELETE'),
  );
}

export async function putAvailability(
  date: string,
  total_beds: number,
  note: string | null,
): Promise<void> {
  await unwrap(
    await jsonRequest(`/api/admin/availability/${date}`, 'PUT', { total_beds, note }),
  );
}

export async function deleteAvailability(date: string): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/availability/${date}`, 'DELETE'));
}

export async function bulkAvailability(
  from: string,
  to: string,
  total_beds: number,
  note: string | null,
): Promise<{ count: number }> {
  return unwrap<{ ok: true; count: number }>(
    await jsonRequest('/api/admin/availability/bulk', 'POST', { from, to, total_beds, note }),
  );
}

export async function rotateGuestPassword(password: string): Promise<void> {
  await unwrap(await jsonRequest('/api/admin/settings/guest-password', 'POST', { password }));
}

export async function rotateAdminPassword(password: string): Promise<void> {
  await unwrap(await jsonRequest('/api/admin/settings/admin-password', 'POST', { password }));
}

export async function setHutCapacity(capacity: number): Promise<void> {
  await unwrap(
    await jsonRequest('/api/admin/settings/hut-capacity', 'POST', { capacity }),
  );
}

export async function getPrices(signal?: AbortSignal): Promise<PricesUpdate> {
  return unwrap<PricesUpdate>(
    await jsonRequest('/api/admin/settings/prices', 'GET', undefined, signal),
  );
}

export async function setPrices(prices: PricesUpdate): Promise<void> {
  await unwrap(await jsonRequest('/api/admin/settings/prices', 'POST', prices));
}

/** Trigger a browser download of the CSV export blob. */
export async function downloadExport(from: string, to: string): Promise<void> {
  const res = await fetch(`/api/admin/export.csv?from=${from}&to=${to}`, {
    method: 'GET',
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* */
    }
    throw new ApiClientError(
      res.status,
      body?.error ?? { code: 'ERR_INTERNAL', message: 'Export failed' },
    );
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `stays_${from}_${to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the hut-duty slot export CSV for the given range. */
export async function downloadSlotsExport(from: string, to: string): Promise<void> {
  const res = await fetch(`/api/admin/export-slots.csv?from=${from}&to=${to}`, {
    method: 'GET',
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* */
    }
    throw new ApiClientError(
      res.status,
      body?.error ?? { code: 'ERR_INTERNAL', message: 'Export failed' },
    );
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `slots_${from}_${to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Prune past hut-duty slots that lie within [from, to]. Returns deleted count. */
export async function pruneHutDutySlots(
  from: string,
  to: string,
): Promise<{ deleted: number }> {
  return unwrap<{ deleted: number }>(
    await jsonRequest('/api/admin/hut-duties/slots/prune', 'POST', { from, to }),
  );
}

/* ─── hut-duty poll: shared types ─────────────────────────────────────── */

export type PollStatus = 'draft' | 'open' | 'closed' | 'finalized';
export type PollSlotState = 'open' | 'locked' | 'closed';

export type PollSummary = {
  id: number;
  title: string;
  status: PollStatus;
  deadline: string | null;
  intro_text: string | null;
  created_at: string;
  slot_count: number;
  participant_count: number;
};

export type PollSlot = {
  id: number;
  poll_id: number;
  check_in: string;
  check_out: string;
  label: string | null;
  state: PollSlotState;
  assignee_name_id: number | null;
  beds_per_night: number[];
  is_duty_per_day: boolean[];
  created_at: string;
  comment_count?: number;
};

export type PollParticipant = {
  id: number;
  name_id: number | null;
  display_name: string | null;
  display_name_set: number;
  token: string;
  email: string | null;
  email_sent_at: string | null;
  invited_at: string;
  last_seen_at: string | null;
  resolved_name: string | null;
};

export type PollClaim = {
  id: number;
  slot_id: number;
  participant_id: number;
  rank: number;
  beds: number;
  status: 'active' | 'withdrawn';
  participant_name: string;
};

export type PollDetail = PollSummary & {
  slots: PollSlot[];
  participants: PollParticipant[];
  claims: PollClaim[];
};

export type PollSlotInputBody = {
  check_in: string;
  check_out: string;
  label?: string | null;
  state?: PollSlotState;
  assignee_name_id?: number | null;
  beds_per_night?: number[];
  is_duty_per_day?: boolean[];
};

/* ─── hut-duty poll: admin ────────────────────────────────────────────── */

export async function listPolls(signal?: AbortSignal): Promise<PollSummary[]> {
  return unwrap<PollSummary[]>(await jsonRequest('/api/admin/polls', 'GET', undefined, signal));
}

export async function createPoll(body: {
  title: string;
  deadline?: string | null;
  intro_text?: string | null;
}): Promise<PollSummary> {
  return unwrap<PollSummary>(await jsonRequest('/api/admin/polls', 'POST', body));
}

export async function fetchPoll(id: number, signal?: AbortSignal): Promise<PollDetail> {
  return unwrap<PollDetail>(await jsonRequest(`/api/admin/polls/${id}`, 'GET', undefined, signal));
}

export async function patchPoll(
  id: number,
  patch: Partial<{ title: string; status: PollStatus; deadline: string | null; intro_text: string | null }>,
): Promise<PollSummary> {
  return unwrap<PollSummary>(await jsonRequest(`/api/admin/polls/${id}`, 'PATCH', patch));
}

export async function deletePoll(id: number): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/polls/${id}`, 'DELETE'));
}

export async function addPollSlot(pollId: number, slot: PollSlotInputBody): Promise<PollSlot> {
  return unwrap<PollSlot>(await jsonRequest(`/api/admin/polls/${pollId}/slots`, 'POST', slot));
}

export async function patchPollSlot(
  pollId: number,
  slotId: number,
  slot: PollSlotInputBody,
): Promise<PollSlot> {
  return unwrap<PollSlot>(
    await jsonRequest(`/api/admin/polls/${pollId}/slots/${slotId}`, 'PATCH', slot),
  );
}

/** Request body for assignPollClaim -- the pre-defaults shape, mirroring
 *  PollAdminClaimInput in @baerenfang/shared (whose inferred type has the
 *  server-side defaults already applied), like PollSlotInputBody above. */
export type PollAdminClaimBody = {
  slot_id: number;
  participant_id: number;
  rank?: number;
  beds?: number | null;
};

/** Put a participant on an open slot. Stored as an ordinary claim, so the
 *  participant sees it as their own and others can still contest it. Omit
 *  `beds` to inherit the slot's own per-night template. */
export async function assignPollClaim(
  pollId: number,
  body: PollAdminClaimBody,
): Promise<PollClaim> {
  return unwrap<PollClaim>(await jsonRequest(`/api/admin/polls/${pollId}/claims`, 'POST', body));
}

export async function deletePollClaim(pollId: number, claimId: number): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/polls/${pollId}/claims/${claimId}`, 'DELETE'));
}

export async function deletePollSlot(pollId: number, slotId: number): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/polls/${pollId}/slots/${slotId}`, 'DELETE'));
}

export async function addPollSlotsBulk(
  pollId: number,
  slots: PollSlotInputBody[],
): Promise<{ added: number }> {
  return unwrap<{ ok: true; added: number }>(
    await jsonRequest(`/api/admin/polls/${pollId}/slots/bulk`, 'POST', { slots }),
  );
}

export async function addPollInvite(
  pollId: number,
  body: { name_id?: number | null; display_name?: string | null; email?: string | null },
): Promise<PollParticipant> {
  return unwrap<PollParticipant>(await jsonRequest(`/api/admin/polls/${pollId}/invites`, 'POST', body));
}

export async function deletePollInvite(pollId: number, participantId: number): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/polls/${pollId}/invites/${participantId}`, 'DELETE'));
}

export async function invitePollKnownEmails(pollId: number): Promise<{ added: number }> {
  return unwrap<{ ok: true; added: number }>(
    await jsonRequest(`/api/admin/polls/${pollId}/invites/known`, 'POST'),
  );
}

export async function invitePollNewEmail(
  pollId: number,
  name: string,
  email: string,
): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/polls/${pollId}/invites/email`, 'POST', { name, email }));
}

export type SendInvitesResult = {
  ok: true;
  sent: number;
  skipped: number;
  failed: { email: string; error: string }[];
};

export async function sendPollInvites(
  pollId: number,
  resend = false,
): Promise<SendInvitesResult> {
  return unwrap<SendInvitesResult>(
    await jsonRequest(`/api/admin/polls/${pollId}/invites/send`, 'POST', { resend }),
  );
}

export async function addAdminSlotComment(
  pollId: number,
  slotId: number,
  body: string,
): Promise<void> {
  await unwrap(await jsonRequest(`/api/admin/polls/${pollId}/slots/${slotId}/comments`, 'POST', { body }));
}

export type FinalizeResult = {
  ok: true;
  imported: number;
  closed_overrides: number;
  // Assignment mails sent to the people who ended up in the roster. Advisory:
  // the import succeeded even when these failed.
  notified: number;
  failed: { email: string; error: string }[];
};

export async function finalizePoll(
  pollId: number,
  opts: { sync_names?: boolean; write_closed_availability?: boolean } = {},
): Promise<FinalizeResult> {
  return unwrap<FinalizeResult>(
    await jsonRequest(`/api/admin/polls/${pollId}/finalize`, 'POST', opts),
  );
}

/* ─── hut-duty poll: participant ──────────────────────────────────────── */

export type PollBoardClaimant = {
  participant_id: number;
  name: string;
  rank: number;
  beds: number;
  is_me: boolean;
};

export type PollWritableReason = 'open' | 'draft' | 'closed' | 'finalized' | 'deadline';

export type PollBoardSlot = {
  id: number;
  check_in: string;
  check_out: string;
  label: string | null;
  state: PollSlotState;
  /** Roster name behind a locked slot, if one is set. */
  assignee_name: string | null;
  comment_count: number;
  active_claims: number;
  claimants: PollBoardClaimant[];
  my_claim: { rank: number; status: 'active' | 'withdrawn'; beds: number } | null;
};

export type PollBoard = {
  poll: { id: number; title: string; status: PollStatus; deadline: string | null; intro_text: string | null };
  writable: boolean;
  writable_reason: PollWritableReason;
  me: { participant_id: number; display_name: string | null; display_name_set: boolean };
  slots: PollBoardSlot[];
};

export type PollThreadComment = {
  id: number;
  participant_id: number | null;
  body: string;
  created_at: string;
  author_name: string | null;
  is_admin: boolean;
  is_me: boolean;
};

export async function enterPoll(
  token: string,
): Promise<{ ok: true; display_name_set: boolean; suggested_name: string }> {
  return unwrap(await jsonRequest('/api/poll/enter', 'POST', { token }));
}

export async function fetchPollBoard(signal?: AbortSignal): Promise<PollBoard> {
  return unwrap<PollBoard>(await jsonRequest('/api/poll/board', 'GET', undefined, signal));
}

export async function setPollName(display_name: string): Promise<void> {
  await unwrap(await jsonRequest('/api/poll/name', 'PATCH', { display_name }));
}

export async function claimPollSlot(slot_id: number, rank: number, beds: number): Promise<void> {
  await unwrap(await jsonRequest('/api/poll/claims', 'POST', { slot_id, rank, beds }));
}

export async function updatePollClaimBeds(slotId: number, beds: number): Promise<void> {
  await unwrap(await jsonRequest(`/api/poll/claims/${slotId}`, 'PATCH', { beds }));
}

export async function withdrawPollClaim(slotId: number): Promise<void> {
  await unwrap(await jsonRequest(`/api/poll/claims/${slotId}`, 'DELETE'));
}

export async function fetchPollThread(slotId: number, signal?: AbortSignal): Promise<PollThreadComment[]> {
  const r = await unwrap<{ comments: PollThreadComment[] }>(
    await jsonRequest(`/api/poll/slots/${slotId}/comments`, 'GET', undefined, signal),
  );
  return r.comments;
}

export async function addPollComment(slotId: number, body: string): Promise<void> {
  await unwrap(await jsonRequest(`/api/poll/slots/${slotId}/comments`, 'POST', { body }));
}
